import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Trophy, Calendar, Clock, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";

const SPORTS = ["football", "basketball", "tennis"];

/**
 * PredictionEditor — embedded inside AdminGameEditor when game.type === "prediction_match".
 *
 * Provides:
 *   • Sport selector (football | basketball | tennis) — drives which teams appear
 *   • Teams library: create / list / delete teams with logo uploads
 *   • Matches editor: add multiple matches with team_a + team_b + kickoff datetime
 *   • Final result entry per match (scores for foot/basket, winner a/b for tennis)
 *   • Winners count input + "Draw winners" button (calls /prediction/draw)
 *
 * Props:
 *   game: full game object (sport, matches, winners_count, ...)
 *   onGameChange(patch): persist top-level game field changes (sport, winners_count)
 *   onReload(): refetch the game (used after matches save / draw)
 */
export default function PredictionEditor({ game, onGameChange, onReload }) {
  const { t } = useTranslation();
  const sport = game.sport || "football";
  const rewardMode = game.prediction_reward_mode || "draw";
  const [teams, setTeams] = useState([]);
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamLogo, setNewTeamLogo] = useState(null);
  const [matches, setMatches] = useState(game.matches || []);
  const [drawing, setDrawing] = useState(false);
  const [stats, setStats] = useState(null);
  const logoInputRef = useRef(null);

  // Load teams whenever sport changes
  useEffect(() => {
    loadTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sport]);

  // Keep local matches in sync when parent game updates
  useEffect(() => {
    setMatches(game.matches || []);
  }, [game.matches]);

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.id]);

  async function loadTeams() {
    try {
      const r = await api.get(`/teams?sport=${sport}`);
      setTeams(r.data.teams);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function loadStats() {
    try {
      const r = await api.get(`/games/${game.id}/prediction/stats`);
      setStats(r.data);
    } catch {
      /* game may have no entries yet */
    }
  }

  async function handleLogoUpload(file) {
    if (!file) return null;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/uploads/team-logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return r.data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    }
  }

  async function createTeam() {
    const name = newTeamName.trim();
    if (!name) return;
    let logo_url = null;
    if (newTeamLogo) {
      logo_url = await handleLogoUpload(newTeamLogo);
    }
    try {
      await api.post("/teams", { sport, name, logo_url });
      toast.success(t("common.save"));
      setNewTeamName("");
      setNewTeamLogo(null);
      if (logoInputRef.current) logoInputRef.current.value = "";
      loadTeams();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function removeTeam(id) {
    if (!window.confirm(t("common.delete") + "?")) return;
    try {
      await api.delete(`/teams/${id}`);
      loadTeams();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  function addMatch() {
    setMatches((prev) => [
      ...prev,
      {
        id: null,
        team_a_id: "",
        team_b_id: "",
        match_datetime: "",
        real_score_a: null,
        real_score_b: null,
        real_winner: null,
      },
    ]);
  }

  function updateMatch(i, patch) {
    setMatches((prev) => prev.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  }

  function removeMatch(i) {
    setMatches((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function saveMatches() {
    // Validate: end_date < earliest match_datetime
    const validMatches = matches.filter(
      (m) => m.team_a_id && m.team_b_id && m.match_datetime
    );
    if (validMatches.length !== matches.length) {
      toast.error(t("game_editor.pred_required"));
      return;
    }
    if (game.end_date) {
      const endDate = new Date(`${game.end_date}T00:00:00+04:00`);
      for (const m of validMatches) {
        const kickoff = new Date(m.match_datetime);
        if (kickoff <= endDate) {
          toast.error(t("game_editor.pred_end_before_match"));
          return;
        }
      }
    }
    try {
      const payload = {
        matches: validMatches.map((m) => ({
          id: m.id,
          team_a_id: m.team_a_id,
          team_b_id: m.team_b_id,
          match_datetime: m.match_datetime,
          real_score_a: m.real_score_a,
          real_score_b: m.real_score_b,
          real_winner: m.real_winner,
        })),
      };
      await api.patch(`/games/${game.id}/matches`, payload);
      toast.success(t("game_editor.pred_matches_saved"));
      onReload && onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function saveResult(i) {
    const m = matches[i];
    if (!m.id) {
      toast.error(t("game_editor.pred_save_matches"));
      return;
    }
    try {
      const payload =
        sport === "tennis"
          ? { real_winner: m.real_winner }
          : { real_score_a: m.real_score_a, real_score_b: m.real_score_b };
      await api.patch(`/games/${game.id}/matches/${m.id}/result`, payload);
      toast.success(t("game_editor.pred_result_saved"));
      onReload && onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function drawWinners() {
    // Require all results saved
    const allDone = matches.every((m) =>
      sport === "tennis"
        ? m.real_winner === "a" || m.real_winner === "b"
        : m.real_score_a != null && m.real_score_b != null
    );
    if (!allDone || matches.length === 0) {
      toast.error(t("game_editor.pred_missing_results"));
      return;
    }
    setDrawing(true);
    try {
      const r = await api.post(`/games/${game.id}/prediction/draw`, {
        winners_count: game.winners_count || 1,
      });
      if (r.data.winners.length === 0) {
        toast.info(
          `Eligible: ${r.data.eligible_count}. ${t("game_editor.pred_matches_saved")}`
        );
      } else {
        toast.success(
          `${r.data.winners.length} winner(s) drawn (from ${r.data.eligible_count} eligible)`
        );
      }
      loadStats();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDrawing(false);
    }
  }

  async function distributePrizes() {
    const allDone = matches.every((m) =>
      sport === "tennis"
        ? m.real_winner === "a" || m.real_winner === "b"
        : m.real_score_a != null && m.real_score_b != null
    );
    if (!allDone || matches.length === 0) {
      toast.error(t("game_editor.pred_missing_results"));
      return;
    }
    if (!(game.guaranteed_prize_label || "").trim()) {
      toast.error(t("game_editor.pred_prize_required"));
      return;
    }
    setDrawing(true);
    try {
      const r = await api.post(`/games/${game.id}/prediction/distribute`);
      if (r.data.eligible_count === 0) {
        toast.info(t("lottery.distribute_none"));
      } else {
        toast.success(
          t("lottery.distribute_success", {
            count: r.data.newly_rewarded + r.data.already_rewarded,
            emails: r.data.emails_sent,
          })
        );
      }
      loadStats();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDrawing(false);
    }
  }

  return (
    <div className="qrise-card p-6 space-y-6" data-testid="prediction-editor">
      {/* Sport + reward mode */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium">
            {t("game_editor.pred_sport")}
          </Label>
          <Select
            value={sport}
            onValueChange={(v) => onGameChange({ sport: v })}
          >
            <SelectTrigger
              className="mt-1.5 rounded-xl h-10"
              data-testid="pred-sport-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SPORTS.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`play.pred_sport_${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-muted-foreground mt-1">
            {t("game_editor.pred_sport_hint")}
          </p>
        </div>
        <div>
          <Label className="text-xs font-medium">
            {t("game_editor.pred_reward_mode")}
          </Label>
          <Select
            value={rewardMode}
            onValueChange={(v) => onGameChange({ prediction_reward_mode: v })}
          >
            <SelectTrigger
              className="mt-1.5 rounded-xl h-10"
              data-testid="pred-reward-mode-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draw">
                {t("game_editor.pred_reward_draw")}
              </SelectItem>
              <SelectItem value="all_winners">
                {t("game_editor.pred_reward_all")}
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[10px] text-muted-foreground mt-1">
            {t("game_editor.pred_reward_mode_hint")}
          </p>
        </div>
      </div>

      {rewardMode === "draw" ? (
        <div>
          <Label className="text-xs font-medium">
            {t("game_editor.winners_count")}
          </Label>
          <Input
            type="number"
            min={1}
            value={game.winners_count ?? 1}
            onChange={(e) =>
              onGameChange({
                winners_count: Math.max(1, parseInt(e.target.value) || 1),
              })
            }
            className="mt-1.5 rounded-xl h-10"
            data-testid="pred-winners-count-input"
          />
        </div>
      ) : (
        <div
          className="rounded-xl border border-amber-400/40 bg-amber-400/5 p-4 space-y-3"
          data-testid="pred-guaranteed-prize-block"
        >
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700 dark:text-amber-300 inline-flex items-center gap-1.5">
            <Gift className="h-3.5 w-3.5" />
            {t("lottery.guaranteed_prize")}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium">
                {t("game_editor.pred_prize_label")}
              </Label>
              <Input
                value={game.guaranteed_prize_label || ""}
                onChange={(e) =>
                  onGameChange({ guaranteed_prize_label: e.target.value })
                }
                placeholder={t("game_editor.pred_prize_label_ph")}
                className="mt-1.5 rounded-xl h-10"
                data-testid="pred-prize-label-input"
              />
            </div>
            <div>
              <Label className="text-xs font-medium">
                {t("game_editor.pred_coupon_code")}
              </Label>
              <Input
                value={game.guaranteed_coupon_code || ""}
                onChange={(e) =>
                  onGameChange({
                    guaranteed_coupon_code: e.target.value || null,
                  })
                }
                className="mt-1.5 rounded-xl h-10 font-mono"
                data-testid="pred-coupon-code-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                {t("game_editor.pred_coupon_code_hint")}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium">
                {t("game_editor.pred_claim_days")}
              </Label>
              <Input
                type="number"
                min={1}
                value={game.guaranteed_claim_days ?? 30}
                onChange={(e) =>
                  onGameChange({
                    guaranteed_claim_days: Math.max(
                      1,
                      parseInt(e.target.value) || 30,
                    ),
                  })
                }
                className="mt-1.5 rounded-xl h-10 max-w-[140px]"
                data-testid="pred-claim-days-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                {t("game_editor.pred_claim_days_hint")}
              </p>
            </div>
            <div>
              <Label className="text-xs font-medium">
                {t("game_editor.pred_claim_deadline")}
              </Label>
              <Input
                type="date"
                value={game.guaranteed_claim_deadline || ""}
                onChange={(e) =>
                  onGameChange({
                    guaranteed_claim_deadline: e.target.value || null,
                  })
                }
                className="mt-1.5 rounded-xl h-10"
                data-testid="pred-claim-deadline-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                {t("game_editor.pred_claim_deadline_hint")}
              </p>
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium">
              {t("game_editor.pred_prize_instructions")}
            </Label>
            <Textarea
              value={game.guaranteed_prize_instructions || ""}
              onChange={(e) =>
                onGameChange({
                  guaranteed_prize_instructions: e.target.value || null,
                })
              }
              placeholder={t("game_editor.pred_prize_instructions_ph")}
              rows={2}
              className="mt-1.5 rounded-xl text-sm"
              data-testid="pred-prize-instructions-input"
            />
          </div>
          <p className="text-[11px] text-muted-foreground leading-snug">
            {t("lottery.emails_auto_note")}
          </p>
        </div>
      )}

      {/* Teams library */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold">
            {t("game_editor.pred_teams_title")}
          </div>
          <div className="text-[10px] text-muted-foreground">
            {teams.length} teams
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground leading-snug mb-3">
          {t("game_editor.pred_teams_hint")}
        </p>
        <div className="flex items-end gap-2 mb-3">
          <div className="flex-1">
            <Input
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              placeholder={t("game_editor.pred_team_name")}
              className="h-9 rounded-lg"
              data-testid="pred-new-team-name"
            />
          </div>
          <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-border cursor-pointer text-xs font-medium hover:bg-muted/40">
            <Upload className="h-3.5 w-3.5" />
            {newTeamLogo ? newTeamLogo.name.slice(0, 10) : t("game_editor.pred_team_logo")}
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => setNewTeamLogo(e.target.files?.[0] || null)}
              data-testid="pred-new-team-logo-input"
            />
          </label>
          <Button
            size="sm"
            onClick={createTeam}
            disabled={!newTeamName.trim()}
            className="h-9 rounded-lg text-xs"
            data-testid="pred-create-team-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            {t("game_editor.pred_team_add")}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 max-h-40 overflow-auto">
          {teams.map((team) => (
            <div
              key={team.id}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-background/60 pl-1.5 pr-1 py-1"
              data-testid={`pred-team-${team.id}`}
            >
              {team.logo_url ? (
                <img
                  src={team.logo_url}
                  alt={team.name}
                  className="h-6 w-6 rounded-full object-cover bg-muted"
                />
              ) : (
                <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold text-muted-foreground">
                  {team.name.slice(0, 2).toUpperCase()}
                </div>
              )}
              <span className="text-xs font-medium pr-1">{team.name}</span>
              <button
                onClick={() => removeTeam(team.id)}
                className="h-6 w-6 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 inline-flex items-center justify-center"
                data-testid={`pred-team-delete-${team.id}`}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
          {teams.length === 0 && (
            <div className="text-xs text-muted-foreground">—</div>
          )}
        </div>
      </div>

      {/* Matches */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold">
            {t("game_editor.pred_matches_title")}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={addMatch}
            className="h-8 rounded-lg text-xs"
            data-testid="pred-add-match-btn"
          >
            <Plus className="h-3 w-3 mr-1" />
            {t("game_editor.pred_match_add")}
          </Button>
        </div>
        <div className="space-y-3 max-h-[480px] overflow-auto pr-1">
          {matches.map((m, i) => (
            <div
              key={i}
              className="p-3 rounded-xl border border-border bg-background/40 space-y-2"
              data-testid={`pred-match-row-${i}`}
            >
              <div className="grid grid-cols-2 gap-2">
                <Select
                  value={m.team_a_id || ""}
                  onValueChange={(v) => updateMatch(i, { team_a_id: v })}
                >
                  <SelectTrigger
                    className="h-9 rounded-lg text-xs"
                    data-testid={`pred-match-teama-${i}`}
                  >
                    <SelectValue placeholder={t("game_editor.pred_match_team_a")} />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map((tt) => (
                      <SelectItem key={tt.id} value={tt.id}>
                        {tt.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={m.team_b_id || ""}
                  onValueChange={(v) => updateMatch(i, { team_b_id: v })}
                >
                  <SelectTrigger
                    className="h-9 rounded-lg text-xs"
                    data-testid={`pred-match-teamb-${i}`}
                  >
                    <SelectValue placeholder={t("game_editor.pred_match_team_b")} />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map((tt) => (
                      <SelectItem key={tt.id} value={tt.id}>
                        {tt.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {t("game_editor.pred_match_datetime")}
                </label>
                <Input
                  type="datetime-local"
                  value={m.match_datetime ? m.match_datetime.slice(0, 16) : ""}
                  onChange={(e) => {
                    const raw = e.target.value; // YYYY-MM-DDTHH:mm
                    // Store as GMT+4 ISO
                    const iso = raw ? `${raw}:00+04:00` : "";
                    updateMatch(i, { match_datetime: iso });
                  }}
                  className="h-9 rounded-lg text-xs mt-1"
                  data-testid={`pred-match-dt-${i}`}
                />
              </div>
              {/* Final result */}
              <div className="pt-2 border-t border-border/60">
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold mb-1.5 flex items-center gap-1">
                  <Trophy className="h-3 w-3" />
                  {t("game_editor.pred_match_result")}
                </div>
                {sport === "tennis" ? (
                  <Select
                    value={m.real_winner || ""}
                    onValueChange={(v) => updateMatch(i, { real_winner: v })}
                  >
                    <SelectTrigger
                      className="h-9 rounded-lg text-xs"
                      data-testid={`pred-match-winner-${i}`}
                    >
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="a">
                        {t("game_editor.pred_match_winner_a")}
                      </SelectItem>
                      <SelectItem value="b">
                        {t("game_editor.pred_match_winner_b")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="number"
                      min={0}
                      value={m.real_score_a ?? ""}
                      onChange={(e) =>
                        updateMatch(i, {
                          real_score_a:
                            e.target.value === "" ? null : parseInt(e.target.value),
                        })
                      }
                      placeholder={t("game_editor.pred_match_score_a")}
                      className="h-9 rounded-lg text-xs"
                      data-testid={`pred-match-scorea-${i}`}
                    />
                    <Input
                      type="number"
                      min={0}
                      value={m.real_score_b ?? ""}
                      onChange={(e) =>
                        updateMatch(i, {
                          real_score_b:
                            e.target.value === "" ? null : parseInt(e.target.value),
                        })
                      }
                      placeholder={t("game_editor.pred_match_score_b")}
                      className="h-9 rounded-lg text-xs"
                      data-testid={`pred-match-scoreb-${i}`}
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => saveResult(i)}
                  className="h-7 rounded-lg text-[10px] text-muted-foreground hover:text-foreground"
                  data-testid={`pred-save-result-${i}`}
                  disabled={!m.id}
                >
                  <Clock className="h-3 w-3 mr-1" />
                  {t("common.save")}
                </Button>
                <button
                  onClick={() => removeMatch(i)}
                  className="h-7 w-7 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 inline-flex items-center justify-center"
                  data-testid={`pred-match-delete-${i}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Button
            size="sm"
            onClick={saveMatches}
            className="h-9 rounded-lg text-xs bg-foreground text-background"
            data-testid="pred-save-matches-btn"
            disabled={matches.length === 0}
          >
            {t("game_editor.pred_save_matches")}
          </Button>
          {rewardMode === "draw" ? (
            <Button
              size="sm"
              onClick={drawWinners}
              disabled={drawing || matches.length === 0}
              className="h-9 rounded-lg text-xs font-bold uppercase tracking-wider"
              data-testid="pred-draw-btn"
              style={{
                background:
                  "linear-gradient(180deg, #f5e4bf 0%, #d4af37 55%, #a98027 100%)",
                color: "#2a1f07",
              }}
            >
              <Trophy className="h-3.5 w-3.5 mr-1" />
              {t("game_editor.pred_draw_btn")}
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={distributePrizes}
              disabled={drawing || matches.length === 0}
              className="h-9 rounded-lg text-xs font-bold uppercase tracking-wider"
              data-testid="pred-distribute-btn"
              style={{
                background:
                  "linear-gradient(180deg, #f5e4bf 0%, #d4af37 55%, #a98027 100%)",
                color: "#2a1f07",
              }}
            >
              <Gift className="h-3.5 w-3.5 mr-1" />
              {t("game_editor.pred_distribute_btn")}
            </Button>
          )}
        </div>
      </div>

      {/* Stats summary */}
      {stats && (
        <div
          className="rounded-xl border border-border/60 bg-muted/20 p-3 text-xs space-y-1"
          data-testid="pred-stats"
        >
          <div className="flex justify-between">
            <span className="text-muted-foreground">Participants</span>
            <span className="font-semibold">{stats.total_participants}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">All-correct</span>
            <span className="font-semibold">{stats.eligible_count}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              {rewardMode === "all_winners"
                ? t("lottery.rewarded_label")
                : "Winners drawn"}
            </span>
            <span className="font-semibold">
              {rewardMode === "all_winners"
                ? (stats.rewarded_count ?? 0)
                : stats.winners_count}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
